/**
 * @fileoverview App Header Component
 * 
 * @description
 * Header corporativo com logo, navegação, context panel e theme toggle.
 * Segue guidelines de design system profissional.
 * 
 * Features:
 * - Logo corporativo Sétima (SVG vetorial)
 * - Navegação horizontal estruturada
 * - Context Panel global (versão DRE, edições, premissas)
 * - Theme toggle (dark/light mode)
 * - Responsivo (mobile-friendly)
 * 
 * @version 1.3.0
 * @author Savio Bertolani
 * @date 2025-10-24
 */

import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Container, 
  Toolbar, 
  Typography, 
  Button,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  IconDashboard,
  IconLineChart,
  IconSettings,
} from '../utils/icons';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { ContextPanel } from './layout/ContextPanel';
import { useState } from 'react';
import { GlossarioModal } from './glossario/GlossarioModal';

/**
 * Navigation items configuration
 * Nova ordem: Histórico → Dashboard → Premissas → DRE Editável
 */
const NAV_ITEMS = [
  {
    label: 'Histórico',
    path: '/historico',
    icon: IconLineChart,
  },
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: IconDashboard,
  },
  {
    label: 'Premissas',
    path: '/premissas',
    icon: IconSettings,
  },
  {
    label: 'DRE Editável',
    path: '/dre-editavel',
    icon: EditNoteIcon,
  },
] as const;

/**
 * AppHeader Component Props
 */
interface AppHeaderProps {
  onToggleTheme?: () => void;
  mode?: 'light' | 'dark';
}

/**
 * AppHeader Component
 * 
 * Corporate header with logo, navigation, context panel and theme toggle.
 * ContextPanel is integrated here to appear consistently across all pages.
 */
export function AppHeader({ onToggleTheme, mode = 'dark' }: AppHeaderProps = {}) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [glossarioOpen, setGlossarioOpen] = useState(false);
  
  return (
    <>
      <GlossarioModal 
        open={glossarioOpen} 
        onClose={() => setGlossarioOpen(false)} 
      />
    <AppBar 
      position="sticky" 
      elevation={2}
      sx={{
        // Adaptável: grafite escuro (dark mode) ou branco (light mode)
        backgroundColor: mode === 'dark' ? 'grey.900' : 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Main Navigation Bar */}
      <Toolbar 
        sx={{ 
          px: { xs: 2, sm: 3, md: 4 }, 
          minHeight: { xs: 56, sm: 64 }, // Altura fixa do toolbar
          height: { xs: 56, sm: 64 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%', height: '100%' }}
          >
            {/* Logo + Title */}
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={2}
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8,
                },
                transition: 'opacity 0.2s',
                height: '100%',
              }}
            >
              {/* Logo Sétima */}
              <Box
                component="img"
                src="/logo-setima.svg"
                alt="Sétima Logo"
                sx={{
                  height: { xs: 28, sm: 32, md: 36 }, // Responsivo: mobile 28px, tablet 32px, desktop 36px
                  width: 'auto', // Mantém aspect ratio do viewBox (131x56 = 2.34:1)
                  maxWidth: { xs: 70, sm: 80, md: 90 }, // Limita largura máxima (aproximado)
                  display: 'block',
                  objectFit: 'contain', // Garante que não distorce
                  flexShrink: 0, // Não permite que shrink quebre proporções
                  // Inverte cor do logo no light mode (logo SVG é branco)
                  filter: mode === 'light' ? 'invert(1)' : 'none',
                }}
              />
              
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 300,
                  color: 'text.primary', // Adaptável ao tema
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                EV Platform
              </Typography>
            </Stack>
            
            {/* Navigation Links */}
            <Stack
              direction="row"
              spacing={{ xs: 1, md: 2 }}
              alignItems="center"
              sx={{
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = currentPath === item.path;
                
                return (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    startIcon={<Icon className="w-5 h-5" />}
                    variant={isActive ? 'contained' : 'text'}
                    sx={{
                      minHeight: 44,
                      px: 3,
                      textTransform: 'none',
                      fontWeight: isActive ? 600 : 400,
                      // Adaptável ao tema
                      color: isActive 
                        ? (mode === 'dark' ? 'grey.900' : 'common.white')  // Ativo: contraste com botão
                        : 'text.primary',                                   // Inativo: texto padrão do tema
                      backgroundColor: isActive ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? 'primary.light' : 'action.hover',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              
              {/* Botão Glossário */}
              <Tooltip title="Glossário Valuation Academy">
                <IconButton
                  onClick={() => setGlossarioOpen(true)}
                  sx={{
                    ml: 1,
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <MenuBookIcon />
                </IconButton>
              </Tooltip>
              
              {/* Theme Toggle Button */}
              {onToggleTheme && (
                <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
                  <IconButton
                    onClick={onToggleTheme}
                    sx={{
                      ml: 1,
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {mode === 'dark' ? (
                      <Brightness7Icon />
                    ) : (
                      <Brightness4Icon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            
            {/* Mobile Navigation - Horizontal Scrollable Tabs (FASE 5.1) */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                display: { xs: 'flex', md: 'none' },
              }}
            >
              <Tabs
                value={currentPath}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  '& .MuiTabs-flexContainer': {
                    gap: 0.5,
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: 'text.secondary',
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main',
                    height: 3,
                  },
                }}
              >
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  
                  return (
                    <Tab
                      key={item.path}
                      value={item.path}
                      component={Link}
                      to={item.path}
                      icon={<Icon className="w-5 h-5" />}
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                        px: 2,
                        // Adaptável ao tema
                        color: 'text.secondary',
                        opacity: currentPath === item.path ? 1 : 0.7,
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          color: 'text.primary',
                          opacity: 1,
                        },
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'action.hover',
                        },
                      }}
                    />
                  );
                })}
              </Tabs>
              
              {/* Botão Glossário (Mobile) */}
              <Tooltip title="Glossário">
                <IconButton
                  onClick={() => setGlossarioOpen(true)}
                  size="small"
                  sx={{
                    color: 'text.primary',
                    flexShrink: 0,
                  }}
                >
                  <MenuBookIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {/* Theme Toggle Button (Mobile) */}
              {onToggleTheme && (
                <IconButton
                  onClick={onToggleTheme}
                  size="small"
                  sx={{
                    color: 'text.primary',
                    flexShrink: 0,
                  }}
                >
                  {mode === 'dark' ? (
                    <Brightness7Icon fontSize="small" />
                  ) : (
                    <Brightness4Icon fontSize="small" />
                  )}
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Container>
      </Toolbar>
      
      {/* Context Panel - Global em todas as páginas */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <ContextPanel />
      </Box>
    </AppBar>
    </>
  );
}

export default AppHeader;
